// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { contentfulViews } from '@glowforge/ui-common';
import { formatPrice } from 'app/util';
import AffirmAsLowAsDisplay from 'app/common/components/affirm/affirm-as-low-as-display';
import './comparison-table-with-images.less';

const { Image } = contentfulViews;

/**
 * Used to associate columns from contentful with pricing data from ecomm core
 */
const columnNameIdToProductMap = {
  'comparison-chart-pro-name': 'pro',
  'comparison-chart-plus-name': 'plus',
  'comparison-chart-basic-name': 'basic',
};

/**
 * Renders an individual cell, either text or boolean
 */
const ValueCell = ({ comparison, index }) => {
  const value = _.get(comparison, `fields.values[${index}]`, {});
  const visible = _.get(value, 'fields.selected', true);
  const type = _.get(value, 'fields.type', 'text');
  let title = '';
  let subTitle = '';
  if (type === 'text') {
    title = _.get(value, 'fields.text', '');
    subTitle = _.get(comparison, 'fields.title', '');
  } else {
    title = _.get(comparison, 'fields.booleanCellTitle', '');
    subTitle = _.get(comparison, 'fields.booleanCellSubtitle', '');
  }
  return (
    <div className='cell'>
      {(visible && (title || subTitle)) ? [
        <p key={'0'} className='title'>{title}</p>,
        <p key={'1'} className='subtitle'>{subTitle}</p>,
      ] : null}
    </div>
  );
};

ValueCell.propTypes = {
  comparison: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * Renders an image, product name and brief description. This is the first cell in each column
 */
const HeaderCell = ({ columnName }) => {
  const columnNameId = _.get(columnName, 'fields.id', '');
  const columnNameSubtitle = _.get(columnName, 'fields.subTitle', '');
  const image = _.get(columnName, 'fields.images[0]');
  const logo = _.get(columnName, 'fields.images[1]');

  return (
    <div className='cell' key={columnNameId}>
      {image ? <Image media={image} /> : null}
      <p className='product-logo'>{logo ? <Image media={logo} /> : null}</p>
      <p className='product-description'>{columnNameSubtitle}</p>
    </div>
  );
};

HeaderCell.propTypes = {
  columnName: PropTypes.shape({}).isRequired,
};

/**
 * Renders a button that directs users to the order page
 */
const BuyNowButton = ({ columnName, products, type }) => {
  const columnNameId = _.get(columnName, 'fields.id', '');
  const product = products[columnNameIdToProductMap[columnNameId]] || {};
  const productId = _.get(product, 'id');
  const button = _.get(columnName, 'fields.callToActionButton.fields', {});

  return (
    <a
      href={`${button.url}?id=${productId}`}
      className={`buyNow button ${type || button.type} ${button.size}`}>
      {button.text}
    </a>
  );
};

BuyNowButton.propTypes = {
  columnName: PropTypes.shape({}).isRequired,
  products: PropTypes.shape({}).isRequired,
  type: PropTypes.string,
};

BuyNowButton.defaultProps = {
  type: '',
};

/**
 * Renders a cell with the product price, affirm pricing information and a Buy Now button
 */
const PriceCell = ({ columnName, products }) => {
  const columnNameId = _.get(columnName, 'fields.id', '');
  const product = products[columnNameIdToProductMap[columnNameId]] || {};
  const price = _.get(product, 'price');

  return (
    <div className='cell price highlighted'>
      <p className='title'>{formatPrice(price)}</p>
      <AffirmAsLowAsDisplay
        amount={price}
        affirmColor='black'
        affirmType='text'
        hideLearnMore
        pageType='product' />
      <BuyNowButton products={products} columnName={columnName} />
    </div>
  );
};

PriceCell.propTypes = {
  columnName: PropTypes.shape({}).isRequired,
  products: PropTypes.shape({}).isRequired,
};

/**
 * Renders a table that allows users to compare product prices and features
 */
const ComparisonTableWithImages = ({ products, sections }) => {
  const key = _.get(sections, '[0].sys.id', '');
  const title = _.get(sections, '[0].fields.title', '');
  const columnNames = _.get(sections, '[0].fields.columnNamesWithImages', []);
  const comparisons = _.get(sections, '[0].fields.comparisons', []);

  /* eslint-disable react/no-array-index-key */
  return (
    <div key={key}>
      <div className='ComparisonTableWithImages'>
        <h2 className='h2'>{title}</h2>
        <div className='desktop-layout'>
          <div className='column-backgrounds'>
            {columnNames.map((columnName, i) => (
              <div
                key={i}
                className='column-background' />
            ))}
          </div>
          <div className='cells'>
            {columnNames.map((columnName, i) => (
              <HeaderCell
                key={`HeaderCell-${i}`}
                columnName={columnName} />
            ))}
            {columnNames.map((columnName, i) => (
              <PriceCell
                key={`PriceCell-${i}`}
                columnName={columnName}
                products={products} />
            ))}
            {comparisons.map((comparison, i) => {
              const values = _.get(comparison, 'fields.values', []);
              return values.map((value, j) => (
                <ValueCell
                  key={`ValueCell-${i}-${j}`}
                  comparison={comparison}
                  index={j} />
              ));
            })}
            {columnNames.map((columnName, i) => (
              <div
                key={`BuyNowButton-${i}`}
                className='cell highlighted'>
                <BuyNowButton
                  columnName={columnName}
                  products={products}
                  type='default' />
              </div>
            ))}
          </div>
        </div>

        <div className='mobile-layout'>
          {columnNames.map((columnName, i) => (
            <div
              key={`Column-${i}`}
              className='column-background'>
              <HeaderCell
                columnName={columnName} />
              <PriceCell
                columnName={columnName}
                products={products} />
              {comparisons.map((comparison, j) => (
                <ValueCell
                  key={`ValueCell-${i}-${j}`}
                  comparison={comparison}
                  index={i} />
              ))}
              <div className='cell highlighted'>
                <BuyNowButton
                  products={products}
                  columnName={columnName}
                  type='default' />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

ComparisonTableWithImages.propTypes = {
  products: PropTypes.shape({}).isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ComparisonTableWithImages;



// WEBPACK FOOTER //
// ./app/common/components/comparison-table-with-images/comparison-table-with-images.jsx